import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Paperclip, Send, BookOpen, X, Ghost, Wind } from "lucide-react";
import AudioRecorder from "../AudioRecorder";
import { useAuth } from "../../context/AuthContext";

export default function MessageInput({
  inputValue,
  setInputValue,
  handleSend,
  handleSendMedia,
  handleOpenResource,
  disabled = false,
  placeholder = "Type your message...",
  isVentMode,
  setIsVentMode,
  onOpenLetter,
  hasUnreadVent = false
}) {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (file.type.startsWith("image/")) {
        setPreviewUrl(URL.createObjectURL(file));
      }
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onTriggerSend = () => {
    if (selectedFile) {
      handleSendMedia(selectedFile);
      clearFile();
    } else if (inputValue.trim()) {
      handleSend();
    }
  };

  return (
    <div className="w-full border-t border-gray-100 dark:border-gray-800 px-2 sm:px-4 py-2 sm:py-3 bg-white dark:bg-gray-900 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] relative z-10">
      {/* File Preview */}
      {selectedFile && (
        <div className="mb-2 p-2 sm:p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 overflow-hidden">
            {previewUrl ? (
              <img src={previewUrl} alt="Preview" className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded-lg" />
            ) : (
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center text-[10px] font-bold">FILE</div>
            )}
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium truncate max-w-[150px] sm:max-w-[200px]">{selectedFile.name}</p>
              <p className="text-[10px] sm:text-xs text-brand-blue">Ready</p>
            </div>
          </div>
          <button onClick={clearFile} className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full text-gray-500">
            <X size={16} />
          </button>
        </div>
      )}

      <div className="flex items-end gap-1.5 sm:gap-3 max-w-4xl mx-auto">
        <div className="flex items-center">
          <AudioRecorder onRecordingComplete={handleSendMedia} />
        </div>

        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*,application/pdf"
          onChange={handleFileSelect}
        />

        {/* Attachment */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || selectedFile}
          className={`p-2 sm:p-3 rounded-full transition-colors ${selectedFile ? 'text-brand-blue bg-blue-50' : 'text-gray-500 hover:text-brand-blue hover:bg-blue-50 dark:hover:bg-gray-800'}`}
          title="Attach file"
        >
          <Paperclip size={20} className="sm:w-[22px] sm:h-[22px] stroke-[1.5]" />
        </button>

        {/* Soul Letter Button (SEEKERS ONLY) - Hidden on very small mobile if space is tight */}
        {onOpenLetter && user?.role === "SEEKER" && (
          <button
            onClick={onOpenLetter}
            disabled={disabled}
            className="p-2 sm:p-3 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-gray-800 rounded-full transition-colors hidden xs:block"
            title="Write a Soul Letter"
          >
            <span className="text-lg sm:text-xl leading-none">✉️</span>
          </button>
        )}

        {/* Vent Mode Toggle */}
        <button
          onClick={() => setIsVentMode(!isVentMode)}
          className={`p-2 sm:p-3 rounded-full transition-all relative ${isVentMode
            ? "bg-gray-800 text-white dark:bg-white dark:text-gray-900 shadow-lg"
            : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            }`}
          title={isVentMode ? "Vent Mode ON" : "Vent Mode OFF"}
        >
          {isVentMode ? <Ghost size={20} className="sm:w-[22px] sm:h-[22px] stroke-[1.5]" /> : <Wind size={20} className="sm:w-[22px] sm:h-[22px] stroke-[1.5]" />}
          {hasUnreadVent && (
            <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-gray-900 animate-pulse"></span>
          )}
        </button>

        {/* Input Field with Soft Focus Bloom */}
        <div className={`flex-1 relative rounded-2xl border transition-all duration-300 ${isVentMode
          ? "bg-gray-100 dark:bg-gray-800 border-gray-400 border-dashed"
          : "bg-gray-50 dark:bg-gray-800 border-transparent focus-within:border-brand-blue/40 focus-within:ring-[3px] focus-within:ring-brand-blue/15 focus-within:shadow-md focus-within:shadow-brand-blue/5"
          }`}>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onTriggerSend()}
            placeholder={
              selectedFile ? "Caption..." :
                isVentMode ? "Vent freely..." :
                  "Message..."
            }
            disabled={disabled || !!selectedFile}
            className="w-full pl-3 sm:pl-4 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-transparent focus:outline-none text-xs sm:text-base transition-colors"
          />
        </div>

        {/* Send Button */}
        <motion.button
          onClick={onTriggerSend}
          disabled={disabled || (!inputValue.trim() && !selectedFile)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`relative p-2.5 sm:p-3 rounded-full flex items-center justify-center transition-all duration-300 overflow-hidden group ${(!inputValue.trim() && !selectedFile) || disabled
            ? "bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed shadow-none"
            : "bg-brand-gradient text-white shadow-[0_0_15px_rgba(99,102,241,0.5)] active:scale-95 ring-2 ring-indigo-500/30 ring-offset-2 ring-offset-white dark:ring-offset-gray-900"
            }`}
        >
          <Send size={18} className="sm:w-[20px] sm:h-[20px] relative z-10 ml-0.5" />
        </motion.button>
      </div>
    </div>
  );
}
